import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { subscribeEmailOptIn } from "../api/marketingEmailOptIn";

const defaultPreferences = {
  promos: true,
  new_services: true,
  events: false,
  tips: false,
};

const parseToken = (search) => {
  const params = new URLSearchParams(search);
  return params.get("token") || "";
};

export default function EmailSubscribe() {
  const location = useLocation();
  const token = useMemo(() => parseToken(location.search), [location.search]);
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await subscribeEmailOptIn({
        token,
        preferences_json: preferences,
      });
      setResult(data || null);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Unable to subscribe. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto w-full max-w-[640px]">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Email Subscription
            </h1>
          </div>
          <div className="px-6 py-6 space-y-4 text-sm md:text-base">
            {!token && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                Missing or invalid token.
              </div>
            )}
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}

            {result ? (
              <div className="space-y-3">
                <div className="text-lg font-semibold text-slate-900">
                  You're subscribed!
                </div>
                {result.coupon_code && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                    Coupon code:{" "}
                    <span className="font-semibold">{result.coupon_code}</span>
                  </div>
                )}
                {result.coupon_valid_to && (
                  <div className="text-sm text-slate-600">
                    Valid until: {String(result.coupon_valid_to).slice(0, 10)}
                  </div>
                )}
                {result.booking_url && (
                  <a
                    href={result.booking_url}
                    className="inline-flex items-center rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-600"
                  >
                    Book Now
                  </a>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="text-slate-700">
                  Subscribe to occasional updates and get your coupon.
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2">
                  <div className="text-xs font-semibold uppercase text-slate-500">
                    Preferred content
                  </div>
                  {Object.keys(preferences).map((key) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 text-sm text-slate-700"
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(preferences[key])}
                        onChange={(e) =>
                          setPreferences((prev) => ({
                            ...prev,
                            [key]: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      {key.replace(/_/g, " ")}
                    </label>
                  ))}
                </div>
                <button
                  type="submit"
                  disabled={!token || loading}
                  className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                >
                  {loading ? "Subscribing..." : "Subscribe & Get Coupon"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
